import React, { useMemo, useRef, useState } from 'react';
import {
  Container,
  Card,
  Button,
  Modal as BootstrapModal,
  Form as BootstrapForm,
} from 'react-bootstrap';
import { Table, Badge as AntdBadge, App } from 'antd';
import { FilterOutlined, PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import type { ColumnType } from 'antd/es/table/interface';
import PageTitle from '@/common/PageTitle/PageTitle';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import dayjs from 'dayjs';
import styles from './genres.module.scss';

import type { Genre, GenreStatus, Subgenre } from '@/features/genres/genresApi';
import {
  useListGenresQuery,
  useCreateGenreMutation,
  useRenameGenreMutation,
  useArchiveGenreMutation,
  useRestoreGenreMutation,
  usePreflightPurgeGenreMutation,
  usePurgeGenreMutation,
  useCreateSubgenreMutation,
  useRenameSubgenreMutation,
  useArchiveSubgenreMutation,
  useRestoreSubgenreMutation,
  usePreflightPurgeSubgenreMutation,
  usePurgeSubgenreMutation,
  extractGenresErrorMessage,
} from '@/features/genres/genresApi';
import {
  CatalogLifecycleTabs,
  CatalogArchiveModal,
  CatalogRestoreModal,
  CatalogPermanentDeleteModal,
  useCatalogPurgeFlow,
  type CatalogLifecycleView,
} from '@/features/catalog-lifecycle';
import {
  useCanEditPage,
  useCanPerformLifecycleDelete,
} from '@/contexts/SuperAdminIdentityContext';

const PAGE_ID = 'genres';

function listStatusForView(view: CatalogLifecycleView): GenreStatus | 'all' {
  if (view === 'archived') return 'deleted';
  return view;
}

function statusBadge(status: GenreStatus) {
  return status === 'deleted' ? (
    <AntdBadge status="default" text="Archived" />
  ) : (
    <AntdBadge status="success" text="Active" />
  );
}

/** Name-editor modal state shared by Add/Rename genre and subgenre flows. */
type NameEditorState =
  | { mode: 'add-genre' }
  | { mode: 'rename-genre'; genreId: string; currentName: string }
  | { mode: 'add-subgenre'; genreId: string; genreName: string }
  | {
      mode: 'rename-subgenre';
      genreId: string;
      subgenreId: string;
      currentName: string;
    };

function nameEditorTitle(state: NameEditorState): string {
  switch (state.mode) {
    case 'add-genre':
      return 'Add New Genre';
    case 'rename-genre':
      return 'Rename Genre';
    case 'add-subgenre':
      return `Add Subgenre to "${state.genreName}"`;
    case 'rename-subgenre':
      return 'Rename Subgenre';
  }
}

const Genres: React.FC = () => {
  const { message } = App.useApp();
  const canEdit = useCanEditPage(PAGE_ID);
  const canLifecycleDelete = useCanPerformLifecycleDelete();

  const [view, setView] = useState<CatalogLifecycleView>('active');
  const listStatus = listStatusForView(view);

  // counts=true — management page shows living-song counts per taxonomy row.
  const { data, isLoading, isFetching } = useListGenresQuery({
    status: listStatus,
    counts: true,
    limit: 200,
  });
  const { data: activeData } = useListGenresQuery({
    status: 'active',
    counts: true,
    limit: 200,
  });
  const { data: archivedData } = useListGenresQuery({
    status: 'deleted',
    counts: true,
    limit: 200,
  });

  const [createGenre, createState] = useCreateGenreMutation();
  const [renameGenre, renameState] = useRenameGenreMutation();
  const [archiveGenre, archiveGenreState] = useArchiveGenreMutation();
  const [restoreGenre, restoreGenreState] = useRestoreGenreMutation();
  const [preflightPurgeGenre] = usePreflightPurgeGenreMutation();
  const [purgeGenre] = usePurgeGenreMutation();
  const [createSubgenre, createSubState] = useCreateSubgenreMutation();
  const [renameSubgenre, renameSubState] = useRenameSubgenreMutation();
  const [archiveSubgenre, archiveSubState] = useArchiveSubgenreMutation();
  const [restoreSubgenre, restoreSubState] = useRestoreSubgenreMutation();
  const [preflightPurgeSubgenre] = usePreflightPurgeSubgenreMutation();
  const [purgeSubgenre] = usePurgeSubgenreMutation();

  const noopResetBatchUi = () => {};

  const genrePurgeFlow = useCatalogPurgeFlow({
    kind: 'genre',
    canLifecycle: canLifecycleDelete,
    message,
    preflight: (id) => preflightPurgeGenre({ genreId: id }).unwrap(),
    purge: ({ id, reason }) => purgeGenre({ genreId: id, reason }).unwrap(),
    // Bulk delete is not part of Genre v1 — the page never calls openBulk.
    bulkPurge: () =>
      Promise.reject(new Error('Bulk permanent delete is not available for genres')),
    extractErrorMessage: extractGenresErrorMessage,
    resetBatchUi: noopResetBatchUi,
  });

  // Genre id captured at open time so purge can hit the nested route. A ref
  // (not state) because openSingle runs the preflight in the same tick as the
  // click — state set in that click would not be visible yet (404 Not Found).
  const subgenrePurgeParentRef = useRef<string>('');
  const subgenrePurgeFlow = useCatalogPurgeFlow({
    kind: 'subgenre',
    canLifecycle: canLifecycleDelete,
    message,
    preflight: (id) =>
      preflightPurgeSubgenre({
        genreId: subgenrePurgeParentRef.current,
        subgenreId: id,
      }).unwrap(),
    purge: ({ id, reason }) =>
      purgeSubgenre({
        genreId: subgenrePurgeParentRef.current,
        subgenreId: id,
        reason,
      }).unwrap(),
    bulkPurge: () =>
      Promise.reject(new Error('Bulk permanent delete is not available for subgenres')),
    extractErrorMessage: extractGenresErrorMessage,
    resetBatchUi: noopResetBatchUi,
  });

  const countArchivedSubgenres = (rows: Genre[] | undefined): number =>
    (rows ?? []).reduce(
      (sum, genre) =>
        sum + (genre.subgenres ?? []).filter((s) => s.status === 'deleted').length,
      0,
    );

  const genres: Genre[] = useMemo(() => {
    const base = data?.genres ?? [];
    if (view !== 'archived') return base;
    // Archived subgenres live under Active parents — surface those parents on
    // the Archived tab too, so archived taxonomy is always findable here.
    const activeParentsWithArchivedSubs = (activeData?.genres ?? []).filter((g) =>
      (g.subgenres ?? []).some((s) => s.status === 'deleted'),
    );
    return [...base, ...activeParentsWithArchivedSubs].sort((a, b) =>
      a.nameLower.localeCompare(b.nameLower),
    );
  }, [data, activeData, view]);

  const activeCount = activeData?.genres != null ? activeData.genres.length : undefined;
  // Archived tab counts all archived taxonomy entries: archived Genres plus
  // archived Subgenres (whose parents may still be Active).
  const archivedCount =
    archivedData?.genres != null && activeData?.genres != null
      ? archivedData.genres.length +
        countArchivedSubgenres(archivedData.genres) +
        countArchivedSubgenres(activeData.genres)
      : undefined;

  /** Nested rows shown for a genre on the current tab. */
  const visibleSubgenres = (genre: Genre): Subgenre[] => {
    const subgenres = genre.subgenres ?? [];
    if (view !== 'archived') return subgenres;
    return subgenres.filter((s) => s.status === 'deleted');
  };

  const [nameEditor, setNameEditor] = useState<NameEditorState | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const nameModalOpen = nameEditor != null;

  const [archivingGenre, setArchivingGenre] = useState<Genre | null>(null);
  const [restoringGenre, setRestoringGenre] = useState<Genre | null>(null);
  const [archivingSubgenre, setArchivingSubgenre] = useState<Subgenre | null>(null);
  const [restoringSubgenre, setRestoringSubgenre] = useState<Subgenre | null>(null);

  const openNameEditor = (state: NameEditorState, initialName = '') => {
    if (!canEdit) return;
    setNameEditor(state);
    setNameDraft(initialName);
  };

  const closeNameEditor = () => {
    setNameEditor(null);
    setNameDraft('');
  };

  const submitNameEditor = async () => {
    if (!nameEditor) return;
    const trimmed = nameDraft.trim();
    if (trimmed.length === 0) return;
    try {
      if (nameEditor.mode === 'add-genre') {
        const created = await createGenre({ name: trimmed }).unwrap();
        message.success(`Created genre "${created.name}"`);
      } else if (nameEditor.mode === 'rename-genre') {
        if (trimmed === nameEditor.currentName) {
          closeNameEditor();
          return;
        }
        const updated = await renameGenre({
          genreId: nameEditor.genreId,
          name: trimmed,
        }).unwrap();
        message.success(`Renamed genre to "${updated.name}"`);
      } else if (nameEditor.mode === 'add-subgenre') {
        const created = await createSubgenre({
          genreId: nameEditor.genreId,
          name: trimmed,
        }).unwrap();
        message.success(`Created subgenre "${created.name}"`);
      } else {
        if (trimmed === nameEditor.currentName) {
          closeNameEditor();
          return;
        }
        const updated = await renameSubgenre({
          genreId: nameEditor.genreId,
          subgenreId: nameEditor.subgenreId,
          name: trimmed,
        }).unwrap();
        message.success(`Renamed subgenre to "${updated.name}"`);
      }
      closeNameEditor();
    } catch (err: unknown) {
      const msg = extractGenresErrorMessage(err);
      if (msg) message.error(msg);
    }
  };

  const submitArchiveGenre = async () => {
    if (!archivingGenre) return;
    try {
      await archiveGenre({ genreId: archivingGenre.genreId }).unwrap();
      message.success('Genre archived — moved to Archived Genres');
      setArchivingGenre(null);
    } catch (err: unknown) {
      message.error(extractGenresErrorMessage(err) || 'Failed to archive genre');
    }
  };

  const submitRestoreGenre = async () => {
    if (!restoringGenre) return;
    try {
      await restoreGenre({ genreId: restoringGenre.genreId }).unwrap();
      message.success('Genre restored — moved to Active Genres');
      setRestoringGenre(null);
    } catch (err: unknown) {
      // 409 when an active genre now uses the name — surface the API message.
      message.error(extractGenresErrorMessage(err) || 'Failed to restore genre');
    }
  };

  const submitArchiveSubgenre = async () => {
    if (!archivingSubgenre) return;
    try {
      await archiveSubgenre({
        genreId: archivingSubgenre.genreId,
        subgenreId: archivingSubgenre.subgenreId,
      }).unwrap();
      message.success('Subgenre archived');
      setArchivingSubgenre(null);
    } catch (err: unknown) {
      message.error(extractGenresErrorMessage(err) || 'Failed to archive subgenre');
    }
  };

  const submitRestoreSubgenre = async () => {
    if (!restoringSubgenre) return;
    try {
      await restoreSubgenre({
        genreId: restoringSubgenre.genreId,
        subgenreId: restoringSubgenre.subgenreId,
      }).unwrap();
      message.success('Subgenre restored');
      setRestoringSubgenre(null);
    } catch (err: unknown) {
      message.error(extractGenresErrorMessage(err) || 'Failed to restore subgenre');
    }
  };

  const renderSubgenres = (genre: Genre) => {
    const subgenres = visibleSubgenres(genre);
    const subColumns: ColumnType<Subgenre>[] = [
      {
        title: 'Subgenre Name',
        dataIndex: 'name',
        sorter: (a, b) => a.nameLower.localeCompare(b.nameLower),
        width: 260,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 110,
        render: (status: GenreStatus) => statusBadge(status),
      },
      {
        title: 'Songs',
        dataIndex: 'songCount',
        width: 90,
        render: (count: number | undefined) => count ?? 0,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 280,
        render: (_: unknown, sub: Subgenre) => (
          <span>
            {canEdit && sub.status === 'active' ? (
              <a
                style={{ marginRight: 12, cursor: 'pointer', color: '#1677ff' }}
                onClick={() =>
                  openNameEditor(
                    {
                      mode: 'rename-subgenre',
                      genreId: sub.genreId,
                      subgenreId: sub.subgenreId,
                      currentName: sub.name,
                    },
                    sub.name,
                  )
                }
              >
                Rename
              </a>
            ) : null}
            {canLifecycleDelete && sub.status === 'active' ? (
              <a
                style={{ marginRight: 12, color: '#e21e26', cursor: 'pointer' }}
                onClick={() => setArchivingSubgenre(sub)}
              >
                Archive
              </a>
            ) : null}
            {canLifecycleDelete && sub.status === 'deleted' ? (
              <>
                <a
                  style={{ marginRight: 12, color: '#1677ff', cursor: 'pointer' }}
                  onClick={() => setRestoringSubgenre(sub)}
                >
                  Restore
                </a>
                <a
                  style={{ color: '#e21e26', cursor: 'pointer' }}
                  onClick={() => {
                    subgenrePurgeParentRef.current = sub.genreId;
                    void subgenrePurgeFlow.openSingle(sub.subgenreId, sub.name);
                  }}
                >
                  Purge
                </a>
              </>
            ) : null}
          </span>
        ),
      },
    ];
    return (
      <Table
        className={styles.subgenreTable}
        columns={subColumns}
        dataSource={subgenres}
        rowKey="subgenreId"
        pagination={false}
        size="small"
        locale={{ emptyText: 'No subgenres yet' }}
      />
    );
  };

  const columns: ColumnType<Genre>[] = [
    {
      title: 'Genre Name',
      dataIndex: 'name',
      sorter: (a, b) => a.nameLower.localeCompare(b.nameLower),
      width: 260,
    },
    ...(view !== 'active'
      ? [
          {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            render: (status: GenreStatus) => statusBadge(status),
          } as ColumnType<Genre>,
        ]
      : []),
    {
      title: 'Songs',
      dataIndex: 'songCount',
      sorter: (a, b) => (a.songCount ?? 0) - (b.songCount ?? 0),
      width: 90,
      render: (count: number | undefined) => count ?? 0,
    },
    {
      title: 'Subgenres',
      key: 'subgenreCount',
      width: 110,
      render: (_: unknown, genre: Genre) => visibleSubgenres(genre).length,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      width: 140,
      render: (value: string) => dayjs(value).format('YYYY-MM-DD'),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      sorter: (a, b) => dayjs(b.updatedAt).unix() - dayjs(a.updatedAt).unix(),
      width: 140,
      render: (value: string) => dayjs(value).format('YYYY-MM-DD'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 320,
      render: (_: unknown, genre: Genre) => (
        <span>
          {canEdit && genre.status === 'active' ? (
            <>
              <a
                style={{ marginRight: 12, cursor: 'pointer', color: '#1677ff' }}
                onClick={() =>
                  openNameEditor(
                    {
                      mode: 'rename-genre',
                      genreId: genre.genreId,
                      currentName: genre.name,
                    },
                    genre.name,
                  )
                }
              >
                Rename
              </a>
              <a
                style={{ marginRight: 12, cursor: 'pointer', color: '#1677ff' }}
                onClick={() =>
                  openNameEditor({
                    mode: 'add-subgenre',
                    genreId: genre.genreId,
                    genreName: genre.name,
                  })
                }
              >
                Add Subgenre
              </a>
            </>
          ) : null}
          {canLifecycleDelete && genre.status === 'active' ? (
            <a
              style={{ marginRight: 12, color: '#e21e26', cursor: 'pointer' }}
              onClick={() => setArchivingGenre(genre)}
            >
              Archive
            </a>
          ) : null}
          {canLifecycleDelete && genre.status === 'deleted' ? (
            <>
              <a
                style={{ marginRight: 12, color: '#1677ff', cursor: 'pointer' }}
                onClick={() => setRestoringGenre(genre)}
              >
                Restore
              </a>
              <a
                style={{ color: '#e21e26', cursor: 'pointer' }}
                onClick={() => void genrePurgeFlow.openSingle(genre.genreId, genre.name)}
              >
                Purge
              </a>
            </>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <Container fluid>
      <Card style={{ borderRadius: 12 }}>
        <Card.Body>
          <Card.Title
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <PageTitle title="Genres" />
            <span style={{ display: 'inline-flex', gap: 12 }}>
              {canEdit ? (
                <Button
                  style={{
                    background: 'white',
                    color: '#e21e26',
                    border: '1px solid #e21e26',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                  onClick={() => openNameEditor({ mode: 'add-genre' })}
                >
                  <PlusOutlined style={{ fontSize: 16 }} />
                  Add Genre
                </Button>
              ) : null}
              <Button
                onClick={() => setView('active')}
                style={{
                  background: 'white',
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontWeight: '500',
                }}
              >
                <FilterOutlined /> Reset All
              </Button>
            </span>
          </Card.Title>

          <CatalogLifecycleTabs
            view={view}
            onChange={setView}
            activeCount={activeCount}
            archivedCount={archivedCount}
            testId="genres-lifecycle-tabs"
          />

          <Table
            className={`legacy-row-height-table ${styles.table}`}
            columns={columns}
            dataSource={genres}
            expandable={{
              expandedRowRender: renderSubgenres,
              rowExpandable: () => true,
            }}
            pagination={{ pageSize: 50, showQuickJumper: true }}
            rowKey="genreId"
            bordered
            scroll={{ x: true }}
            loading={{
              spinning: isLoading || isFetching,
              indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
            }}
          />
        </Card.Body>
      </Card>

      <BootstrapModal show={nameModalOpen} onHide={closeNameEditor} size="lg">
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>
            {nameEditor ? nameEditorTitle(nameEditor) : ''}
          </BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapForm
          onSubmit={(event) => {
            event.preventDefault();
            void submitNameEditor();
          }}
        >
          <BootstrapModal.Body>
            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>
                {nameEditor?.mode === 'add-subgenre' ||
                nameEditor?.mode === 'rename-subgenre'
                  ? 'Subgenre Name *'
                  : 'Genre Name *'}
              </BootstrapForm.Label>
              <BootstrapForm.Control
                type="text"
                placeholder={
                  nameEditor?.mode === 'add-subgenre' ||
                  nameEditor?.mode === 'rename-subgenre'
                    ? 'Enter subgenre name'
                    : 'Enter genre name'
                }
                value={nameDraft}
                maxLength={256}
                onChange={(e) => setNameDraft(e.target.value)}
                data-testid="genre-name-input"
              />
            </BootstrapForm.Group>
          </BootstrapModal.Body>
          <BootstrapModal.Footer>
            <Button type="button" variant="secondary" onClick={closeNameEditor}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                nameDraft.trim().length === 0 ||
                createState.isLoading ||
                renameState.isLoading ||
                createSubState.isLoading ||
                renameSubState.isLoading
              }
              style={{ background: '#e21e26', border: 'none' }}
            >
              {nameEditor?.mode === 'add-genre' || nameEditor?.mode === 'add-subgenre'
                ? 'Create'
                : 'Save Changes'}
            </Button>
          </BootstrapModal.Footer>
        </BootstrapForm>
      </BootstrapModal>

      <CatalogArchiveModal
        kind="genre"
        open={archivingGenre != null}
        name={archivingGenre?.name}
        loading={archiveGenreState.isLoading}
        onCancel={() => setArchivingGenre(null)}
        onConfirm={() => void submitArchiveGenre()}
      />
      <CatalogRestoreModal
        kind="genre"
        open={restoringGenre != null}
        name={restoringGenre?.name}
        loading={restoreGenreState.isLoading}
        onCancel={() => setRestoringGenre(null)}
        onConfirm={() => void submitRestoreGenre()}
      />
      <CatalogArchiveModal
        kind="subgenre"
        open={archivingSubgenre != null}
        name={archivingSubgenre?.name}
        loading={archiveSubState.isLoading}
        onCancel={() => setArchivingSubgenre(null)}
        onConfirm={() => void submitArchiveSubgenre()}
      />
      <CatalogRestoreModal
        kind="subgenre"
        open={restoringSubgenre != null}
        name={restoringSubgenre?.name}
        loading={restoreSubState.isLoading}
        onCancel={() => setRestoringSubgenre(null)}
        onConfirm={() => void submitRestoreSubgenre()}
      />

      <CatalogPermanentDeleteModal {...genrePurgeFlow.modalProps} />
      <CatalogPermanentDeleteModal {...subgenrePurgeFlow.modalProps} />
    </Container>
  );
};

export default withAuthenticationRequired(Genres);
